import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import dateAdd from "date-fns/add";
import styled from "styled-components/macro";

import {
  IStepValuePlanIntake,
  IWizardHostState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOST_STEPS_PATHS
} from "../../../interfaces";
import { UserAPI } from "../../../../../API/userApi";
import { getStepErrorsFromResponse } from "../../../utils";
import {
  CalendlyCalendar,
  TCalendlyScheduledEvent
} from "../../../../Calendly";
import { useContextHospi } from "../../../../../context/ContextHospi";
import { formatAsUTCToServerDateTime } from "../../../../../utils/time";
import { LoadingIndicator } from "../../../../LoadingIndicator";
import { useGetCalendlyHostIntakeMeetingUrl } from "../../../../Calendly/utils";
import { PlanedIntakeInsuranceModal } from "../../../PlanedIntakeInsuranceModal";

const statePath = WIZARD_HOST_STEPS_PATHS.PlanIntake;

export const WizardStepPlanIntake: TWizardStepComponentEditable<IWizardHostState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IStepValuePlanIntake>>(
      state[statePath] ?? {
        from_dt: null,
        to_dt: null,
        event_id: null,
        done: false
      }
    );
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);
    const [
      isShownPlanedIntakeInsuranceModal,
      setIsShownPlanedIntakeInsuranceModal
    ] = useState(false);
    const isPlanedIntakeInsuranceModalRef = useRef(false);
    let calendlyUrl = useGetCalendlyHostIntakeMeetingUrl();

    useImperativeHandle(ref, () => ({
      save: async () => {
        setErrors(null);
        if (isPlanedIntakeInsuranceModalRef.current) {
          setIsShownPlanedIntakeInsuranceModal(true);
          isPlanedIntakeInsuranceModalRef.current = false;
          return Promise.resolve(null);
        } else {
          if (isShownPlanedIntakeInsuranceModal)
            setIsShownPlanedIntakeInsuranceModal(false);
          try {
            const result = await UserAPI.wizardHostSavePlanIntake(values);
            return {
              ...state,
              [statePath]: result
            };
          } catch (e) {
            setErrors(getStepErrorsFromResponse(e));
            return await Promise.reject(e);
          }
        }
      }
    }));

    return (
      <>
        <WizardStepPlanIntakeView
          values={values}
          setValues={setValues}
          errors={errors}
          readonly={readonly}
          viewonly={viewonly}
          firstName={
            state[WIZARD_HOST_STEPS_PATHS.PersonalInfo]?.first_name ?? ""
          }
          lastName={
            state[WIZARD_HOST_STEPS_PATHS.PersonalInfo]?.last_name ?? ""
          }
          meetingUrl={calendlyUrl}
          setIsPlanedIntakeInsuranceModal={(isPlanedIntakeInsuranceModal) => {
            isPlanedIntakeInsuranceModalRef.current =
              isPlanedIntakeInsuranceModal;
          }}
        />
        {isShownPlanedIntakeInsuranceModal && (
          <PlanedIntakeInsuranceModal
            onClose={() => setIsShownPlanedIntakeInsuranceModal(false)}
          />
        )}
      </>
    );
  });

const Root = styled.div`
  position: relative;
  min-height: 400px;
`;

const LoaderContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;
const LoaderContainerText = styled.span`
  margin-right: 16px;
  font-size: 24px;
`;
const BlockOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  font-size: 24px;
`;
const BlockOverlayInner = styled.div`
  padding: 16px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
`;

const Description1 = styled.div`
  margin-bottom: 32px;
`;
const Description2 = styled.div`
  font-weight: 500;
`;

export const WizardStepPlanIntakeView: TWizardStepComponentEditableView<
  IStepValuePlanIntake,
  {
    firstName: string;
    lastName: string;
    meetingUrl: string;
  }
> = ({
  values,
  firstName,
  lastName,
  meetingUrl,
  setValues,
  errors,
  readonly,
  viewonly,
  setIsPlanedIntakeInsuranceModal
}) => {
  const { currentUserEmail } = useContextHospi();
  const [isInitialized, setIsInitialized] = useState(false);
  const { t } = useTranslation("host");

  const handleInitialized = useCallback(() => {
    setIsInitialized(true);
  }, []);

  const handleDateAndTimeSubmitted = useCallback(() => {
    typeof setIsPlanedIntakeInsuranceModal === "function" &&
      setIsPlanedIntakeInsuranceModal(true);
  }, [setIsPlanedIntakeInsuranceModal]);

  const handleEventScheduled = useCallback(
    (event: TCalendlyScheduledEvent) => {
      typeof setIsPlanedIntakeInsuranceModal === "function" &&
        setIsPlanedIntakeInsuranceModal(false);

      setValues({
        from_dt: formatAsUTCToServerDateTime(dateAdd(new Date(), { days: 1 })),
        to_dt: formatAsUTCToServerDateTime(
          dateAdd(new Date(), { days: 1, hours: 1 })
        ),
        event_id: event.eventId,
        done: true
      });
    },
    [setValues, setIsPlanedIntakeInsuranceModal]
  );

  return (
    <Root>
      <Description1>
        {t("wizard.steps.PlanIntake.formDescription1")}
      </Description1>
      <Description2>
        {t("wizard.steps.PlanIntake.formDescription2")}
      </Description2>
      {readonly || viewonly ? (
        <h3>
          {values.done
            ? t("wizard.steps.PlanIntake.intakeDone")
            : t("wizard.steps.PlanIntake.intakeNotDone")}
        </h3>
      ) : (
        <>
          {!isInitialized ? (
            <LoaderContainer>
              <LoaderContainerText>
                {t("wizard.steps.PlanIntake.loadingCalendar")}
              </LoaderContainerText>
              <LoadingIndicator fontSize={32} />
            </LoaderContainer>
          ) : null}
          <CalendlyCalendar
            meetingUrl={meetingUrl}
            userName={`${firstName} ${lastName}`}
            userEmail={currentUserEmail}
            onInitialized={handleInitialized}
            onEventScheduled={handleEventScheduled}
            onDateAndTimeSelected={handleDateAndTimeSubmitted}
          />
          {isInitialized && values.done ? (
            <BlockOverlay>
              <BlockOverlayInner>
                {t("wizard.steps.PlanIntake.timeslotSelected")} <br />{" "}
                {t("wizard.steps.PlanIntake.timeslotSelectedTwo")}
              </BlockOverlayInner>
            </BlockOverlay>
          ) : null}
          {errors?.done ? (
            <div
              style={{
                color: "red"
              }}
            >
              {errors.done}
            </div>
          ) : null}
        </>
      )}
    </Root>
  );
};
